/**
 * rum_page_loads — DB-persisted public RUM telemetry for the unauthenticated
 * page-load pipeline.
 *
 * Fixes the critical instrumentation gap where /api/admin/metrics/client
 * (auth-gated) silently dropped all timing beacons from unauthenticated
 * users visiting /auth/login, leaving the most important user journey
 * completely unobservable.
 *
 * All timing values are integers in milliseconds, relative to
 * PerformanceNavigationTiming.startTime (i.e. relative to navigationStart).
 * NULL means the milestone was not reached or not instrumented.
 *
 * tenant_id is nullable — unauthenticated sessions always have NULL.
 */
import { pgTable, text, integer, timestamp, uuid, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const rumPageLoads = pgTable(
  "rum_page_loads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sessionId: text("session_id").notNull(),
    page: text("page").notNull(),
    navigationStartTs: timestamp("navigation_start_ts", { withTimezone: true }),
    domContentLoadedMs: integer("dom_content_loaded_ms"),
    loadEventEndMs: integer("load_event_end_ms"),
    firstReactRenderMs: integer("first_react_render_ms"),
    authMeDispatchMs: integer("auth_me_dispatch_ms"),
    authMeResponseMs: integer("auth_me_response_ms"),
    splashDismissedMs: integer("splash_dismissed_ms"),
    fcpMs: integer("fcp_ms"),
    lcpMs: integer("lcp_ms"),
    ttfbMs: integer("ttfb_ms"),
    connectionType: text("connection_type"),
    ipHash: text("ip_hash"),
    tenantId: text("tenant_id"),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    sessionIdx: index("idx_rum_page_loads_session_id").on(t.sessionId),
    occurredAtIdx: index("idx_rum_page_loads_occurred_at").on(t.occurredAt),
    pageIdx: index("idx_rum_page_loads_page").on(t.page),
  }),
);

export const insertRumPageLoadSchema = createInsertSchema(rumPageLoads).omit({
  id: true,
  occurredAt: true,
});

export type InsertRumPageLoad = z.infer<typeof insertRumPageLoadSchema>;
export type RumPageLoad = typeof rumPageLoads.$inferSelect;
